/**
 * Yankı — Phaser scene. Rules live in ./core/game.ts (pure TS); this file draws, plays the sequence and routes input.
 * Levels come from the shared level map (`?level=N` deep links open one directly). Player text: ./i18n.ts.
 */
import Phaser from "phaser";
import { sfx, unlockAudio } from "@shared/audio";
import { exposeBotHook, loadSave, writeSave } from "@kit/boot";
import { publicGameUrl } from "@kit/lang";
import { afterSkip, afterWin, mapBack, mapText, openMap, starLine } from "@kit/map";
import { recordSkip, recordWin } from "@kit/progress";
import { track } from "@kit/telemetry";
import { BG, FONT, PANEL, PANEL_2, TEXT_DIM, W, backdrop, button, centerCamera, confetti, overlay, share, text, topBar } from "@kit/ui";
import { REVERSE_FROM, canSkip, createGame, enterPad, nextRound, playbackFor, responseFor, starsFor, startInput, startShowing, type EchoState } from "./core/game";
import { t } from "./i18n";

const PAD_NAMES = ["padSun", "padWave", "padMoon", "padStar"] as const;
const PAD_COLORS = [0xffca3a, 0x2ec4b6, 0x8ac926, 0xff595e] as const;
const PAD_W = 262;
const PAD_H = 190;
const SLOT_Y = 408;
/** Layout for the 1280-tall design; taller phones move the pads and buttons down and grow the pads (`fit`). */
const PADS_Y = 556;
const ACTION_Y = 1020;
const SKIP_Y = 1120;

/** Mistakes per level survive leaving the level, so the free skip (H4) can't be lost by reopening it. */
type Save = { failLevel: number; fails: number };

export class YankiScene extends Phaser.Scene {
  private n = 1;
  private state!: EchoState;
  private save!: Save;
  private startedAt = 0;
  private over = false;
  private fit = { dy: 0, grow: 0 };
  private hud!: ReturnType<typeof topBar>;
  private modeLabel!: Phaser.GameObjects.Text;
  private modeBg!: Phaser.GameObjects.Rectangle;
  private lengthLabel!: Phaser.GameObjects.Text;
  private statusLabel!: Phaser.GameObjects.Text;
  private detailLabel!: Phaser.GameObjects.Text;
  private progressLabel!: Phaser.GameObjects.Text;
  private slots!: Phaser.GameObjects.Container;
  private pads: Phaser.GameObjects.Container[] = [];
  private glows: Phaser.GameObjects.Rectangle[] = [];
  private action?: Phaser.GameObjects.Container;
  private skipButton?: Phaser.GameObjects.Container;
  private ov?: Phaser.GameObjects.Container;
  private runToken = 0;

  constructor() {
    super("yanki");
  }

  init(data: { level?: number }): void {
    this.n = Math.max(1, Math.floor(data?.level ?? 1));
    this.pads = [];
    this.glows = [];
    this.action = undefined;
    this.skipButton = undefined;
    this.ov = undefined;
    this.over = false;
  }

  create(): void {
    centerCamera(this);
    this.cameras.main.setBackgroundColor(BG);
    backdrop(this, { light: 0x2ec4b6 });
    // ?bot=1&fast=N (smokes on slow software GL, like Çarşı): timers and tweens run N× faster. Players never see it.
    const q = new URLSearchParams(location.search);
    const fast = q.has("bot") ? Number(q.get("fast")) : 1;
    this.tweens.timeScale = this.time.timeScale = fast > 1 ? fast : 1;
    this.save = loadSave<Save>("yanki", { failLevel: 0, fails: 0 });
    this.state = createGame(this.n);
    this.startedAt = Date.now();
    const reverse = this.state.spec.reverse;
    const extra = Math.max(0, this.scale.height - 1280);
    this.fit = { dy: Math.min(220, extra * 0.5), grow: Math.min(60, extra * 0.15) };

    this.hud = topBar(this, t("topTitle", { n: this.n }), t("roundLabel"), mapBack(this, "yanki"));
    const accent = reverse ? 0xffca3a : 0x2ec4b6;
    this.modeBg = this.add.rectangle(W / 2, 192, 560, 54, accent, 0.16).setStrokeStyle(3, accent, 0.9);
    this.modeLabel = text(this, W / 2, 192, reverse ? t("reverse") : t("forward"), 26, reverse ? "#ffca3a" : "#2ec4b6");
    this.lengthLabel = text(this, W / 2, 246, "", 20, TEXT_DIM);
    this.statusLabel = text(this, W / 2, 296, "", 30, "#ffffff");
    this.detailLabel = text(this, W / 2, 346, "", 20, "#b8bee6", false).setWordWrapWidth(620);
    this.slots = this.add.container(0, 0);
    this.makePads();
    this.progressLabel = text(this, W / 2, 940 + this.below(), "", 18, TEXT_DIM, false);
    text(this, W / 2, 1200 + this.below(), t("controls"), 17, TEXT_DIM, false);
    if (reverse && this.n === REVERSE_FROM) {
      this.tweens.add({ targets: [this.modeBg, this.modeLabel], scale: { from: 1.12, to: 1 }, duration: 420, yoyo: true, repeat: 2, ease: "Sine.InOut" });
    }

    this.input.keyboard?.on("keydown", this.onKey, this);
    this.events.once("shutdown", () => {
      this.runToken++;
      this.input.keyboard?.off("keydown", this.onKey, this);
    });
    track("level_start", { level: this.n, reverse });
    this.refresh();
    exposeBotHook({
      state: () => {
        const camera = this.cameras.main;
        const cssScale = this.game.canvas.getBoundingClientRect().width / this.scale.width;
        const screenPoint = (x: number, y: number) => ({ x: (x - camera.scrollX) * camera.zoom * cssScale, y: (y - camera.scrollY) * camera.zoom * cssScale });
        return {
          level: this.n,
          reverse,
          round: this.state.round,
          rounds: this.state.spec.rounds,
          phase: this.state.phase,
          input: this.state.typed.length,
          errors: this.state.errors,
          canSkip: !!this.skipButton,
          response: responseFor(this.state),
          screen: { action: screenPoint(W / 2, ACTION_Y + this.below()), skip: screenPoint(W / 2, SKIP_Y + this.below()), pads: this.pads.map((pad) => screenPoint(pad.x, pad.y)) },
        };
      },
      autoplay: () => this.autoplay(),
      share: () => this.doShare(),
    });
  }

  /** Shift for everything under the pads on a tall phone. */
  private below(): number {
    return this.fit.dy + this.fit.grow;
  }

  private makePads(): void {
    const PAD_H_FIT = PAD_H + this.fit.grow;
    const top = PADS_Y + this.fit.dy;
    const positions = [
      { x: 205, y: top },
      { x: 515, y: top },
      { x: 205, y: top + 224 + this.fit.grow },
      { x: 515, y: top + 224 + this.fit.grow },
    ];
    PAD_NAMES.forEach((name, index) => {
      const { x, y } = positions[index]!;
      const color = PAD_COLORS[index]!;
      const base = this.add.graphics();
      base.fillStyle(PANEL, 1).fillRoundedRect(-PAD_W / 2, -PAD_H_FIT / 2, PAD_W, PAD_H_FIT, 28);
      base.lineStyle(3, color, 0.72).strokeRoundedRect(-PAD_W / 2, -PAD_H_FIT / 2, PAD_W, PAD_H_FIT, 28);
      const glow = this.add.rectangle(0, 0, PAD_W - 8, PAD_H_FIT - 8, color, 0).setAlpha(0);
      const icon = this.add.graphics().setPosition(0, -17);
      this.drawSymbol(icon, index, color);
      const label = this.add.text(0, 51, t(name), { fontFamily: FONT, fontSize: "23px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0.5);
      const key = this.add.text(-PAD_W / 2 + 22, -PAD_H_FIT / 2 + 19, String(index + 1), { fontFamily: FONT, fontSize: "16px", color: `#${color.toString(16).padStart(6, "0")}`, fontStyle: "bold" }).setOrigin(0.5);
      const pad = this.add.container(x, y, [base, glow, icon, label, key]).setSize(PAD_W, PAD_H_FIT);
      pad.setInteractive({ useHandCursor: true });
      pad.on("pointerdown", (_pointer: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();
        this.activatePad(index);
      });
      this.pads.push(pad);
      this.glows.push(glow);
    });
  }

  private drawSymbol(g: Phaser.GameObjects.Graphics, index: number, color: number, scale = 1, cut: number = PANEL): void {
    g.fillStyle(color, 1);
    g.lineStyle(7 * scale, color, 1);
    if (index === 0) {
      g.fillCircle(0, 0, 25 * scale);
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI * 2 * i) / 8;
        g.lineBetween(Math.cos(a) * 34 * scale, Math.sin(a) * 34 * scale, Math.cos(a) * 45 * scale, Math.sin(a) * 45 * scale);
      }
    } else if (index === 1) {
      for (let row = -1; row <= 1; row++) {
        const y = row * 15 * scale;
        g.beginPath();
        g.moveTo(-37 * scale, y);
        g.lineTo(-18 * scale, y - 11 * scale);
        g.lineTo(0, y);
        g.lineTo(18 * scale, y + 11 * scale);
        g.lineTo(37 * scale, y);
        g.strokePath();
      }
    } else if (index === 2) {
      g.fillCircle(-4 * scale, 0, 32 * scale);
      g.fillStyle(cut, 1).fillCircle(10 * scale, -12 * scale, 27 * scale);
      g.lineStyle(5 * scale, color, 0.85).lineBetween(-27 * scale, 27 * scale, 12 * scale, -8 * scale);
    } else {
      const points: Phaser.Geom.Point[] = [];
      for (let i = 0; i < 10; i++) {
        const angle = -Math.PI / 2 + (i * Math.PI) / 5;
        const radius = (i % 2 === 0 ? 39 : 18) * scale;
        points.push(new Phaser.Geom.Point(Math.cos(angle) * radius, Math.sin(angle) * radius));
      }
      g.fillPoints(points, true);
    }
  }

  /**
   * One slot per symbol of the round. Entered symbols fill in the order the player must enter them: left to right
   * forward, right to left on reverse levels (the slot to fill next is ringed), so the rule is visible while typing.
   */
  private drawSlots(): void {
    this.slots.removeAll(true);
    const len = this.state.pattern.length;
    const size = len > 9 ? 40 : 46;
    const gap = 10;
    const total = len * size + (len - 1) * gap;
    const x0 = W / 2 - total / 2 + size / 2;
    const reverse = this.state.spec.reverse;
    const typed = this.state.typed;
    const g = this.add.graphics();
    this.slots.add(g);
    for (let k = 0; k < len; k++) {
      const x = x0 + k * (size + gap);
      // Position k (left to right) holds the k-th symbol played; reverse levels fill it from the right.
      const filledIndex = reverse ? len - 1 - k : k;
      const filled = filledIndex < typed.length;
      const next = this.state.phase === "input" && filledIndex === typed.length;
      g.fillStyle(PANEL_2, 1).fillRoundedRect(x - size / 2, SLOT_Y - size / 2, size, size, 10);
      if (next) g.lineStyle(3, reverse ? 0xffca3a : 0x2ec4b6, 1).strokeRoundedRect(x - size / 2, SLOT_Y - size / 2, size, size, 10);
      if (filled) {
        const pad = typed[filledIndex]!;
        const icon = this.add.graphics().setPosition(x, SLOT_Y);
        this.drawSymbol(icon, pad, PAD_COLORS[pad]!, size / 110, PANEL_2);
        this.slots.add(icon);
      }
    }
    if (this.state.phase === "input" || this.state.phase === "mistake") {
      // The arrow points the way the slots fill.
      const ax = reverse ? x0 - size / 2 - 26 : x0 + total - size / 2 + 26;
      this.slots.add(text(this, ax, SLOT_Y, reverse ? "←" : "→", 30, reverse ? "#ffca3a" : "#2ec4b6"));
    }
  }

  private onKey(event: KeyboardEvent): void {
    if (event.repeat) return;
    const number = Number(event.key);
    if (number >= 1 && number <= 4) this.activatePad(number - 1);
  }

  private activatePad(index: number): void {
    if (this.state.phase !== "input" || this.over) return;
    unlockAudio();
    this.flash(index, 150);
    sfx.pop(index + 2);
    this.state = enterPad(this.state, index);
    if (this.state.phase === "mistake") this.onMistake();
    if (this.state.phase === "success") sfx.win();
    this.refresh();
    if (this.state.phase === "won") void this.finish();
    else if (this.state.phase === "success" && this.action) {
      this.tweens.add({ targets: this.action, scale: 1.035, duration: 150, yoyo: true, ease: "Sine.InOut" });
    }
  }

  private onMistake(): void {
    sfx.nope();
    this.cameras.main.shake(120, 0.004);
    this.save = this.save.failLevel === this.n ? { ...this.save, fails: this.save.fails + 1 } : { failLevel: this.n, fails: 1 };
    writeSave("yanki", this.save);
  }

  private flash(index: number, duration: number): void {
    const glow = this.glows[index]!;
    const pad = this.pads[index]!;
    this.tweens.killTweensOf(glow);
    this.tweens.killTweensOf(pad);
    glow.setAlpha(0.58);
    this.tweens.add({ targets: glow, alpha: 0, duration, ease: "Quad.Out" });
    this.tweens.add({ targets: pad, scale: 0.96, duration: Math.min(100, duration / 2), yoyo: true, ease: "Quad.Out" });
  }

  private handleAction(): void {
    unlockAudio();
    if (this.over) return;
    if (this.state.phase === "ready" || this.state.phase === "mistake" || this.state.phase === "input") {
      void this.playSequence();
    } else if (this.state.phase === "success") {
      this.state = nextRound(this.state);
      void this.playSequence();
    }
  }

  private async playSequence(): Promise<void> {
    const token = ++this.runToken;
    this.state = startShowing(this.state);
    this.refresh();
    await this.wait(360);
    for (const pad of playbackFor(this.state)) {
      if (token !== this.runToken) return;
      this.flash(pad, Math.round(this.state.spec.tempo * 0.48));
      sfx.pop(pad + 2);
      await this.wait(this.state.spec.tempo);
    }
    if (token !== this.runToken) return;
    this.state = startInput(this.state);
    this.refresh();
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => this.time.delayedCall(ms, resolve));
  }

  private refresh(): void {
    const { spec, phase, round } = this.state;
    const reverse = spec.reverse;
    const length = this.state.pattern.length;
    this.hud.value.setText(`${round}/${spec.rounds}`);
    this.lengthLabel.setText(reverse && this.n === REVERSE_FROM && phase === "ready" ? t("reverseNew") : t("length", { len: length }));
    this.lengthLabel.setColor(reverse && this.n === REVERSE_FROM && phase === "ready" ? "#ffca3a" : TEXT_DIM);
    this.progressLabel.setText(t("progress", { replays: this.state.errors }));

    let status = t("readyStatus");
    let detail = reverse ? t("readyReverse") : t("readyDetail");
    let actionLabel = t("listen");
    let actionColor = 0x2ec4b6;
    if (phase === "showing") {
      status = t("showingStatus");
      detail = reverse ? t("showingReverse") : t("showingForward");
      actionLabel = t("playing");
    } else if (phase === "input") {
      status = reverse ? t("inputReverse") : t("inputStatus");
      detail = t("inputDetail", { typed: this.state.typed.length, len: length });
      actionLabel = t("listenAgain");
      actionColor = 0x6a4c93;
    } else if (phase === "mistake") {
      status = t("mistakeStatus");
      detail = reverse ? t("mistakeReverse") : t("mistakeDetail");
      actionLabel = t("freeReplay");
      actionColor = 0xff924c;
    } else if (phase === "success") {
      status = t("successStatus", { round });
      detail = t("successDetail");
      actionLabel = t("oneMore");
      actionColor = 0x8ac926;
    } else if (phase === "won") {
      status = t("won");
      detail = "";
    }
    this.statusLabel.setText(status);
    this.detailLabel.setText(detail);
    this.drawSlots();
    this.action?.destroy();
    this.action = undefined;
    if (phase !== "won") {
      const onClick = phase === "showing" ? () => undefined : () => this.handleAction();
      this.action = button(this, W / 2, ACTION_Y + this.below(), 360, 82, actionLabel, actionColor, onClick);
    }
    const skippable = !this.over && phase !== "won" && this.save.failLevel === this.n && canSkip(this.save.fails);
    if (skippable && !this.skipButton) this.skipButton = button(this, W / 2, SKIP_Y + this.below(), 360, 64, t("skip"), 0x6a4c93, () => this.skip());
    if (!skippable && this.skipButton) {
      this.skipButton.destroy();
      this.skipButton = undefined;
    }
  }

  private secs(): number {
    return Math.round((Date.now() - this.startedAt) / 1000);
  }

  private skip(): void {
    if (this.over) return;
    this.over = true;
    this.runToken++;
    track("level_end", { level: this.n, result: "skip", errors: this.state.errors, secs: this.secs() });
    this.save = { failLevel: 0, fails: 0 };
    writeSave("yanki", this.save);
    openMap(this, "yanki", afterSkip(this.n, recordSkip("yanki", this.n)));
  }

  private async finish(): Promise<void> {
    if (this.over) return;
    this.over = true;
    this.refresh();
    const token = this.runToken;
    const stars = starsFor(this.state.errors);
    track("level_end", { level: this.n, result: "won", errors: this.state.errors, secs: this.secs() });
    sfx.win();
    confetti(this);
    this.save = { failLevel: 0, fails: 0 };
    writeSave("yanki", this.save);
    const outcome = recordWin("yanki", this.n, stars);
    await this.wait(650);
    if (token !== this.runToken) return;
    const sub = `${starLine(stars)}\n${t("wonSub", { n: this.n, len: this.state.pattern.length, replays: this.state.errors })}`;
    this.ov = overlay(this, t("won"), sub, [
      { label: t("share"), color: 0x1982c4, onClick: () => void this.doShare() },
      { label: mapText("continue"), color: 0x2ec4b6, onClick: () => openMap(this, "yanki", afterWin(this.n, outcome)) },
    ]);
  }

  private async doShare(): Promise<void> {
    track("share_click", { level: this.n });
    const ok = await share(t("shareText", { n: this.n, len: this.state.pattern.length }), `${publicGameUrl("yanki")}?level=${this.n}&utm_source=share`, t("shareTitle"));
    if (ok) track("share_done", { level: this.n });
  }

  /** Plays the whole level from the rules (no playback); resolves when it is won. */
  private async autoplay(): Promise<void> {
    const token = ++this.runToken;
    while (!this.over && token === this.runToken) {
      if (this.state.phase === "ready" || this.state.phase === "mistake") this.state = startInput(startShowing(this.state));
      if (this.state.phase !== "input") break;
      for (const pad of responseFor(this.state)) {
        this.flash(pad, 90);
        this.state = enterPad(this.state, pad);
        this.refresh();
        await this.wait(55);
      }
      if (this.state.phase === "won") {
        await this.finish();
        break;
      }
      if (this.state.phase !== "success") break;
      this.state = nextRound(this.state);
      this.refresh();
    }
  }
}
